/**
 * ValueEditSheet - Slide-up bottom sheet with a face-value button grid
 * for editing a dice recognition result.
 * @module ValueEditSheet
 */

import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from '@/components/ui/drawer';
import { useIsLandscape } from '@/hooks/useIsLandscape';
import { cn } from '@/lib/utils';
import { getDiceMax } from '@/services/recognition';

/** Props for the ValueEditSheet component. */
export interface ValueEditSheetProps {
  /** Whether the sheet is open */
  open: boolean;
  /** Callback when open state changes */
  onOpenChange: (open: boolean) => void;
  /** Current dice value (highlighted in grid) */
  value: number;
  /** Dice type string (e.g. "d20") to determine grid size */
  diceType: string;
  /** Called when a new value is selected */
  onValueEdit: (newValue: number) => void;
}

/**
 * Slide-up bottom sheet containing a face-value button grid for correcting
 * a dice recognition result. Slides from bottom in portrait, right in landscape.
 * For d100 (maxValue > 20) shows a number input instead of a grid.
 */
export function ValueEditSheet({
  open,
  onOpenChange,
  value,
  diceType,
  onValueEdit
}: ValueEditSheetProps) {
  const isLandscape = useIsLandscape();
  const maxValue = getDiceMax(diceType);
  const [d100Input, setD100Input] = useState(value);

  /** Sync d100 input when value prop changes (e.g. reopening the sheet) */
  useEffect(() => {
    setD100Input(value);
  }, [value]);

  const isD100 = maxValue > 20;
  const isSmallGrid = maxValue <= 8;

  return (
    <Drawer open={open} onOpenChange={onOpenChange} direction={isLandscape ? 'right' : 'bottom'}>
      <DrawerContent
        direction={isLandscape ? 'right' : 'bottom'}
        className={cn(isLandscape ? 'w-72' : 'max-h-[60vh]')}
      >
        <DrawerHeader>
          <DrawerTitle className="text-glow-subtle">Correct Value</DrawerTitle>
        </DrawerHeader>

        <div className="flex-1 overflow-y-auto p-4">
          {isD100 ? (
            <div className="flex flex-col items-center gap-3">
              <input
                type="number"
                min={1}
                max={100}
                value={d100Input}
                onChange={(e) => setD100Input(Number(e.target.value))}
                className="w-24 text-center text-4xl font-bold h-16 rounded-md border border-accent bg-background/50 text-primary focus:outline-none focus:ring-2 focus:ring-ring"
              />
              <Button
                variant="accent"
                onClick={() => {
                  const clamped = Math.max(1, Math.min(100, d100Input));
                  onValueEdit(clamped);
                  onOpenChange(false);
                }}
              >
                Confirm
              </Button>
            </div>
          ) : (
            <div className={cn('grid', isSmallGrid ? 'grid-cols-4 gap-1' : 'grid-cols-5 gap-0.5')}>
              {Array.from({ length: maxValue }, (_, i) => i + 1).map((n) => (
                <Button
                  key={n}
                  variant={n === value ? 'accent' : 'outline'}
                  className={cn(
                    'text-sm font-bold',
                    isSmallGrid ? 'min-h-[44px] min-w-[44px]' : 'min-h-[36px] min-w-[36px]'
                  )}
                  onClick={() => {
                    onValueEdit(n);
                    onOpenChange(false);
                  }}
                >
                  {n}
                </Button>
              ))}
            </div>
          )}
        </div>
      </DrawerContent>
    </Drawer>
  );
}
